import { PdfViewerFieldPMapping } from '@cumu/shared';
import { controller, target } from '@github/catalyst';
import { PdfRasterizeInstructionPage, processPdfInstruction } from '../../pdf';
import { PDFViewerBase } from './pdf-viewer-base';
import { AcroFormField, PDFDocumentProxy } from './pdfjs';

const rasterDpi = 200;
const rasterJpegQuality = 0.92;
const annotationModeEnableStorage = 3;

@controller('pdf-viewer')
export class PdfViewerElement extends PDFViewerBase {
  @target declare valuesScript: HTMLScriptElement;
  filledPromise = Promise.withResolvers<void>();
  signatureFields: { field: AcroFormField; value: string }[] = [];

  onAnnotationFieldClick: undefined;
  onAnnotationFieldFocus: undefined;
  onCtrlS: undefined;
  onAnnotationEditorLayerRendered: undefined;
  onAnnotationEditorUiManager: undefined;
  onAnnotationEditorModeChanged: undefined;
  onAnnotationFieldChange: undefined;

  connectedCallback() {
    super.connectedCallback();
    const src = this.iframe.getAttribute('deferred-src');
    if (src) {
      this.iframe.setAttribute('src', src);
    }
  }

  async onPdfDocumentLoaded(pdfDocument: PDFDocumentProxy) {
    const values: PdfViewerFieldPMapping[] = JSON.parse(
      this.valuesScript.textContent!
    );
    this.log('Getting field objects');
    const acroForm = await pdfDocument.getFieldObjects();
    if (!acroForm) {
      this.log('No field objects');
      this.filledPromise.resolve();
      return;
    }
    this.log('Filling form');
    for (const { id, name, type, export_values, value } of values) {
      const fieldsOfType =
        acroForm.get(name)?.filter(field => field.type === type) ?? [];
      const fieldsById = fieldsOfType.filter(field => field.id === id);
      const fields = fieldsById.length
        ? fieldsById
        : fieldsOfType.filter(field => field.exportValues === export_values);
      if (!fields?.length) {
        console.warn(
          `Field ${name} with export value ${export_values} not found`
        );
        continue;
      }

      if (value && /^\[[sd]:[a-z]:r *\]$/.test(value)) {
        this.signatureFields.push(...fields.map(field => ({ field, value })));
        continue;
      }

      for (const field of fields) {
        if (field.value === value) {
          continue;
        }

        pdfDocument.annotationStorage.setValue(field.id, {
          value:
            field.type === 'checkbox' || field.type === 'radiobutton'
              ? value === 'On'
              : (value ?? undefined)
        });
      }
    }
    this.log('Filled form');
    this.filledPromise.resolve();
  }

  onPdfLoaded(): void {
    this.iframe.contentWindow?.addEventListener('change', event => {
      if (this.isAnnotationField(event.target)) {
        this.dispatchEvent(
          new CustomEvent('pdf-viewer-user-field-changed', { bubbles: true })
        );
      }
    });
  }

  async save({
    rasterizeEncrypted = false
  }: {
    rasterizeEncrypted?: boolean;
  } = {}): Promise<Uint8Array<ArrayBufferLike>> {
    // Keep using the viewer save path rather than PDFDocumentProxy.saveDocument
    // directly: downloadOrSave also commits active editors and dispatches the
    // PDF scripting WillSave/DidSave events. Capture that path's output rather
    // than downloading it.
    // https://github.com/mozilla/pdf.js/blob/v6.4.299/web/app.js
    const [viewerApplication] = await Promise.all([
      this.pdfLoaded.promise,
      this.filledPromise.promise
    ]);
    let data: Uint8Array<ArrayBufferLike>;
    const downloadManager = viewerApplication.downloadManager;
    viewerApplication.downloadManager = {
      download(d) {
        data = d;
      }
    };
    try {
      await viewerApplication.downloadOrSave();
    } finally {
      viewerApplication.downloadManager = downloadManager;
    }
    const pdfDocument = viewerApplication.pdfDocument;
    // PDF.js can open encrypted PDFs, but its saved bytes remain encrypted.
    // EncryptFilterName identifies an encrypted document even when it doesn't
    // declare any permission restrictions, so rebuild those pages from their
    // rendered output before pdf-lib merges them.
    if (rasterizeEncrypted && pdfDocument) {
      const { info } = await pdfDocument.getMetadata();
      if (info.EncryptFilterName !== null) {
        return this.rasterize(pdfDocument);
      }
    }
    return Uint8Array.from(data!);
  }

  private async rasterize(
    pdfDocument: PDFDocumentProxy
  ): Promise<Uint8Array<ArrayBufferLike>> {
    const pages: PdfRasterizeInstructionPage[] = [];
    const scale = rasterDpi / 72;
    const renderDocument = this.iframe.contentDocument;
    if (!renderDocument) {
      throw new Error('Unable to access PDF viewer document');
    }

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      const page = await pdfDocument.getPage(pageNumber);
      const pageSize = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale });
      // PDF.js installs embedded fonts in the viewer iframe's FontFaceSet.
      // Render there so the canvas can resolve those temporary font families.
      const canvas = renderDocument.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to create canvas context for encrypted PDF');
      }
      await page.render({
        canvasContext: context,
        viewport,
        annotationMode: annotationModeEnableStorage,
        annotationStorage: pdfDocument.annotationStorage,
        background: 'white'
      }).promise;
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          value =>
            value
              ? resolve(value)
              : reject(new Error('Unable to render encrypted PDF page')),
          'image/jpeg',
          rasterJpegQuality
        )
      );
      pages.push({
        data: new Uint8Array(await blob.arrayBuffer()),
        width: pageSize.width,
        height: pageSize.height
      });
    }

    return processPdfInstruction({ type: 'rasterize', pages });
  }
}
